/**
 * Persistence for the Auth0 refresh token — a high-value durable credential,
 * deliberately kept OUT of `window.MetaShipGlobalState` / the `vuex` mirror
 * (mirrors legacy's `setAuth0RefreshToken` action, which is NOT pushed to
 * `updateExternalState`).
 */
import { refreshAuth0Tokens } from "./auth0";

const REFRESH_TOKEN_KEY = "metaship:auth0RefreshToken";
const REFRESH_LOCK_NAME = "metaship:auth0-refresh";

export function getStoredAuth0RefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredAuth0RefreshToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // best-effort
  }
}

/**
 * JWTs are base64URL (RFC 7515 §2: `-`/`_`, no padding) — plain `atob()`
 * throws on `-`/`_` (it only accepts standard `+`/`/` base64), so a payload
 * segment that happens to contain either character (common — it's an
 * arbitrary-byte encoding of arbitrary JSON) silently failed to decode
 * before this translated the alphabet back. Also decodes through
 * `TextDecoder` rather than reading `atob()`'s output directly as JSON text
 * — `atob` yields a Latin-1 binary string, which mangles any non-ASCII
 * character in the payload (e.g. an accented firstname/surname) into
 * mojibake if parsed as-is.
 */
function decodeJwtPayload(bearer: string | null | undefined): Record<string, unknown> | null {
  try {
    const raw = (bearer || "").replace(/^Bearer\s+/i, "");
    const segment = raw.split(".")[1];
    if (!segment) return null;
    let base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder("utf-8").decode(bytes));
  } catch {
    return null;
  }
}

/** Decode a bearer JWT's `exp` claim to a millisecond epoch, or `null` if it can't be read. */
export function decodeJwtExpiryMs(bearer: string | null | undefined): number | null {
  const payload = decodeJwtPayload(bearer);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
}

export interface MetaShipTokenUser {
  id: number;
  firstname: string;
  surname: string;
  emailAddress: string;
  tagId: number | null;
}

/**
 * Decode the numeric MetaShip identity out of a minted MetaShip JWT — the
 * SAME `{ id, firstname, surname, emailAddress, tagId, userID }` payload
 * `refreshUserToken` signs (`metaship-backend/Services/UserAccess/
 * user-access-controller.ts:209-216`). The Auth0 id_token alone never carries
 * this — only a token minted via `POST /user-access/refresh-token` does.
 */
export function decodeMetaShipUser(bearer: string | null | undefined): MetaShipTokenUser | null {
  const payload = decodeJwtPayload(bearer);
  if (!payload || typeof payload.id !== "number") return null;
  return {
    id: payload.id,
    firstname: typeof payload.firstname === "string" ? payload.firstname : "",
    surname: typeof payload.surname === "string" ? payload.surname : "",
    emailAddress: typeof payload.emailAddress === "string" ? payload.emailAddress : "",
    tagId: typeof payload.tagId === "number" ? payload.tagId : null,
  };
}

/**
 * Refresh the Auth0 credential, serialised across every tab of this origin
 * via the Web Locks API (falls back to running unserialised where
 * unsupported — Safari < 15.4, or a non-browser test environment). Auth0
 * ROTATES refresh tokens and revokes the whole family on reuse, so two tabs
 * racing to refresh at once can otherwise kill the session for both. The
 * stored token is re-read AFTER the lock is acquired — another tab may have
 * already rotated it while this one was waiting.
 */
export async function refreshAuth0TokensLocked(): Promise<{ id_token: string | null; refresh_token: string | null }> {
  const run = async () => {
    const rt = getStoredAuth0RefreshToken();
    if (!rt) throw new Error("no Auth0 refresh token");
    const fresh = await refreshAuth0Tokens(rt);
    if (fresh.refresh_token) setStoredAuth0RefreshToken(fresh.refresh_token);
    return fresh;
  };
  if (typeof navigator === "undefined" || !("locks" in navigator) || !navigator.locks) {
    return run();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, run);
}
